/** @jsx jsx */
import { jsx } from '@emotion/react';
// eslint-disable-next-line no-unused-vars
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Card, Button, Elevation } from '@blueprintjs/core';
import { DndProvider } from 'react-dnd';
import Backend from 'react-dnd-html5-backend';
import update from 'immutability-helper';
import { Icon } from '/components/icon';
import DraggableContainer from 'components/multiselect/draggable';
import { multiselectStyles } from './styles';
import { v4 as uuidv4 } from 'uuid';

const convertToArrayOfObjects = (array) => {
	const initialValue = [];
	array.forEach((value) => {
		initialValue.push({
			id: uuidv4(),
			text: value,
		});
	});
	return initialValue;
};

/**
 * Renders blueprint dialog for multi-select.
 */
export default function MultiSelectPopup({ closeModal, items, title, initialSelected = items, minimumItems = 1 }) {
	const initialUnselected = items.filter((item) => !initialSelected.includes(item));
	const itemsAsObjects = convertToArrayOfObjects(items);
	const initialSelectedAsObjects = convertToArrayOfObjects(initialSelected);
	const initialUnselectedAsObjects = convertToArrayOfObjects(initialUnselected);

	const [selected, setSelected] = useState(initialSelectedAsObjects);

	const [unselected, setUnselected] = useState(initialUnselectedAsObjects);

	const moveSelected = useCallback(
		(draggingIndex, hoveringIndex) => {
			const dragItem = selected[draggingIndex];
			setSelected(
				update(selected, {
					$splice: [
						[draggingIndex, 1],
						[hoveringIndex, 0, dragItem],
					],
				})
			);
		},
		[selected]
	);

	const moveUnselected = useCallback(
		(draggingIndex, hoveringIndex) => {
			const dragItem = unselected[draggingIndex];
			setUnselected(
				update(unselected, {
					$splice: [
						[draggingIndex, 1],
						[hoveringIndex, 0, dragItem],
					],
				})
			);
		},
		[unselected]
	);

	const handleSelect = (newValue) => {
		setSelected((selected) => [...selected, newValue]);
		setUnselected(unselected.filter((item) => item !== newValue));
	};

	const handleUnselect = (newValue) => {
		if (selected.length <= minimumItems) return;
		setUnselected((selected) => [...selected, newValue]);
		setSelected(selected.filter((item) => item !== newValue));
	};

	const handleSelectAll = () => {
		if (itemsAsObjects !== selected) {
			setSelected(itemsAsObjects);
			setUnselected([]);
		}
	};

	// Don't want to allow the user to unselect all (causes system to crash),
	//    this doesn't do anything if the items are at n (the minimium number)
	//    if there are more than the minimum number, select the first n
	const handleUnselectAll = () => {
		if (selected.length <= minimumItems) return;
		setSelected(itemsAsObjects.slice(0, minimumItems));
		setUnselected(itemsAsObjects.slice(minimumItems));
	};

	const handleReset = () => {
		setSelected(initialSelectedAsObjects);
		setUnselected(initialUnselectedAsObjects);
	};

	const handleClose = () => {
		handleSelectAll();
		handleSubmit();
	};

	const handleSubmit = () => {
		const selectedToReturn = selected.map((item) => item.text);
		const unselectedToReturn = unselected.map((item) => item.text);
		const returnObject = { selected: selectedToReturn, unselected: unselectedToReturn };
		closeModal(returnObject);
	};

	const { wrapper, cardWrapper, selectCard, centerSection, middleButton, itemTitle } = multiselectStyles;

	return (
		<Dialog
			icon="select"
			onClose={handleClose}
			title={title}
			isOpen
			autoFocus
			canEscapeKeyClose
			canOutsideClickClose
			enforceFocus
			usePortal
			css={wrapper}
		>
			<div css={cardWrapper}>
				<Card elevation={Elevation.TWO} css={selectCard}>
					<h5 css={itemTitle}>
						<div>Unselected</div>
					</h5>
					<DndProvider backend={Backend}>
						<DraggableContainer items={unselected} handleReorder={moveUnselected} toggleSelected={handleSelect} />
					</DndProvider>
				</Card>
				<Card elevation={Elevation.TWO} css={centerSection}>
					<Button onClick={handleSubmit} css={middleButton}>
						<Icon iconSize={30} icon="confirm"></Icon>
					</Button>
					<Button onClick={handleReset} css={middleButton}>
						<Icon iconSize={30} icon="reset"></Icon>
					</Button>
					<Button onClick={handleSelectAll} css={middleButton}>
						<Icon iconSize={30} icon="double-chevron-right"></Icon>
					</Button>
					<Button onClick={handleUnselectAll} css={middleButton}>
						<Icon iconSize={30} icon="double-chevron-left"></Icon>
					</Button>
				</Card>
				<Card elevation={Elevation.TWO} css={selectCard}>
					<h5 css={itemTitle}>
						<div>Selected</div>
					</h5>
					<DndProvider backend={Backend}>
						<DraggableContainer
							items={selected}
							handleReorder={moveSelected}
							toggleSelected={handleUnselect}
						></DraggableContainer>
					</DndProvider>
				</Card>
			</div>
		</Dialog>
	);
}

MultiSelectPopup.propTypes = {
	/**
	 * Function to close modal on click. Returns selected and unselected values;
	 */
	closeModal: PropTypes.func,
	/**
	 * Items to be selected.
	 */
	items: PropTypes.array,
	/**
	 * Items that start off as selected in the component.
	 * Optional, default behavior is start off with all items selected.
	 */
	initialSelected: PropTypes.array,
	/**
	 * This string is used as a title for the dialog.
	 */
	title: PropTypes.string,
};
